import { ref } from 'vue'
import type { Ref } from 'vue'
import type { WizardForm, StoreWizardFormPayload, UpdateWizardFormPayload } from '@/types/wizard-forms'
import {
  listWizardForms as apiListWizardForms,
  createWizardForm as apiCreateWizardForm,
  getWizardForm as apiGetWizardForm,
  updateWizardForm as apiUpdateWizardForm,
  deleteWizardForm as apiDeleteWizardForm,
} from '@/api/wizard-forms'

const forms = ref<WizardForm[]>([])
const form = ref<WizardForm | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchForms(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    forms.value = await apiListWizardForms()
  } catch {
    error.value = 'wizard_forms.list.error_load'
  } finally {
    loading.value = false
  }
}

async function fetchForm(formId: number): Promise<void> {
  loading.value = true
  error.value = null
  try {
    form.value = await apiGetWizardForm(formId)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createForm(payload: StoreWizardFormPayload): Promise<WizardForm> {
  const created = await apiCreateWizardForm(payload)
  forms.value = [...forms.value, created]
  return created
}

async function updateForm(formId: number, payload: UpdateWizardFormPayload): Promise<WizardForm> {
  const updated = await apiUpdateWizardForm(formId, payload)
  form.value = updated
  forms.value = forms.value.map((f) => (f.id === formId ? updated : f))
  return updated
}

async function deleteForm(formId: number): Promise<void> {
  await apiDeleteWizardForm(formId)
  forms.value = forms.value.filter((f) => f.id !== formId)
}

export function useWizardForms(): {
  forms: Ref<WizardForm[]>
  form: Ref<WizardForm | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchForms: () => Promise<void>
  fetchForm: (formId: number) => Promise<void>
  createForm: (payload: StoreWizardFormPayload) => Promise<WizardForm>
  updateForm: (formId: number, payload: UpdateWizardFormPayload) => Promise<WizardForm>
  deleteForm: (formId: number) => Promise<void>
} {
  return {
    forms,
    form,
    loading,
    error,
    fetchForms,
    fetchForm,
    createForm,
    updateForm,
    deleteForm,
  }
}
